import { Location } from '@angular/common';
import { ChangeDetectorRef, Component, EmbeddedViewRef, HostListener, Input, OnChanges, OnDestroy, SimpleChanges, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import * as momentJalaali from 'moment-jalaali';
import { GurdServiceService } from 'src/app/Service/gurd-service.service';
import { NotificationService } from 'src/app/Service/notification.service';
import { OtherService } from 'src/app/Service/other.service';
import { ReportContext, ReportDefinition } from '../../../report.models';
import { toEnglishDigits } from '../../../report.utils';
import { RouteFunctionReportApiService } from '../route-function-report-api.service';
import { RouteAnalysisTableComponent } from './route-analysis-table.component';
import { RouteActionEvent, RouteAnalysisRow, RouteAnalysisStatus, RouteDetailKind, RouteDetailRequest, RouteModalHandle, RouteOption, RouteReportModalState, RouteSummaryFilters, RouteTableAction, RouteTableColumn, VehicleOption } from './route-analysis-report.models';
import { ANALYSIS_ACTIONS, DELETE_ROUTE_ACTION, DETAIL_DEFINITIONS, ROUTE_MODAL_IDS, STATION_MAP_ACTION, analysisColumns, detailColumns, emptyRouteModalState } from './route-analysis-report.utils';
import { RouteSummaryByDayModalComponent } from './components/route-summary-by-day-modal/route-summary-by-day-modal.component';
import { RouteDriverSalaryModalComponent } from './components/route-driver-salary-modal/route-driver-salary-modal.component';
import { RoutePassengersModalComponent } from './components/route-passengers-modal/route-passengers-modal.component';
import { RouteIntermediateStationsModalComponent } from './components/route-intermediate-stations-modal/route-intermediate-stations-modal.component';
import { RouteDetailModalPresenter } from './components/route-report-modal/route-detail-modal.presenter';

declare const $: (selector: string) => RouteModalHandle;

@Component({ selector: 'app-route-analysis-report', templateUrl: './route-analysis-report.component.html' })
export class RouteAnalysisReportComponent implements OnChanges, OnDestroy {
  @Input() context!: ReportContext;
  @Input() definition!: ReportDefinition;
  @ViewChild(RouteAnalysisTableComponent) table?: RouteAnalysisTableComponent;
  @ViewChild(RouteSummaryByDayModalComponent) summaryModal?: RouteSummaryByDayModalComponent;
  @ViewChild(RouteDriverSalaryModalComponent) salaryModal?: RouteDriverSalaryModalComponent;
  @ViewChild(RoutePassengersModalComponent) passengersModal?: RoutePassengersModalComponent;
  @ViewChild(RouteIntermediateStationsModalComponent) stationsModal?: RouteIntermediateStationsModalComponent;
  readonly modalStates: Record<RouteDetailKind, RouteReportModalState> = {
    'daily-summary': emptyRouteModalState(), salary: emptyRouteModalState(), passengers: emptyRouteModalState(), stations: emptyRouteModalState(),
  };
  get detailTable(): RouteAnalysisTableComponent | undefined { return this.activeModal?.table; }
  private get activeModal(): RouteDetailModalPresenter | undefined {
    switch (this.detail?.kind) {
      case 'daily-summary': return this.summaryModal;
      case 'salary': return this.salaryModal;
      case 'passengers': return this.passengersModal;
      case 'stations': return this.stationsModal;
      default: return undefined;
    }
  }
  @ViewChild('moreMenu') private moreMenuTemplate!: TemplateRef<unknown>;
  moreMenuOpen = false;
  private moreMenuView?: EmbeddedViewRef<unknown>;
  private moreMenuElement?: HTMLElement;
  private moreMenuTrigger?: HTMLElement;
  private readonly closeMenuOnScroll = () => this.closeMoreMenu(false);
  readonly form = new FormGroup({
    iRoute: new FormControl(-1), strUnitID: new FormControl(''),
    strSDate: new FormControl(''), strEDate: new FormControl(''), bSchedule: new FormControl(false),
  });
  routes: RouteOption[] = [];
  vehicles: VehicleOption[] = [];
  rows: RouteAnalysisRow[] = [];
  columns: RouteTableColumn[] = [];
  actions: RouteTableAction[] = [];
  detailRows: RouteAnalysisRow[] = [];
  detailColumns: RouteTableColumn[] = [];
  detailActions: RouteTableAction[] = [];
  detail: RouteDetailRequest | null = null;
  detailLoaded = false;
  filtersOpen = true;
  loading = false;
  hasError = false;
  errorMessage = '';
  hasRequestedData = false;
  private version = 0;
  private loadedDates = '';
  private detailDates = '';
  private detailClosing = false;
  private detailReturnFocus?: HTMLElement;
  private destroyed = false;
  private failedStage: 'options' | 'report' | 'detail' = 'options';
  private readonly filterSubscription = this.form.valueChanges.subscribe(() => this.table?.goToPage(1));

  constructor(
    private readonly api: RouteFunctionReportApiService,
    private readonly gurd: GurdServiceService,
    private readonly notify: NotificationService,
    private readonly other: OtherService,
    private readonly router: Router,
    private readonly location: Location,
    private readonly viewContainer: ViewContainerRef,
    private readonly changeDetector: ChangeDetectorRef,
  ) {}
  ngOnChanges(changes: SimpleChanges): void {
    if ((!changes['context'] && !changes['definition']) || !this.context) return;
    this.closeMoreMenu(false);
    this.activeModal?.hide(); this.detailClosing = false;
    for (const kind of Object.keys(this.modalStates) as RouteDetailKind[]) this.modalStates[kind] = emptyRouteModalState();
    this.version++; this.loading = false; this.form.enable({ emitEvent: false });
    this.columns = analysisColumns(this.context.type);
    this.actions = this.canDelete ? [...ANALYSIS_ACTIONS, DELETE_ROUTE_ACTION] : [...ANALYSIS_ACTIONS];
    this.rows = []; this.detail = null; this.hasError = false; this.hasRequestedData = false;
    this.filtersOpen = true; this.loadedDates = '';
    this.form.reset({ iRoute: -1, strUnitID: '', strSDate: momentJalaali().subtract(1, 'day').format('jYYYY/jMM/jDD'), strEDate: momentJalaali().format('jYYYY/jMM/jDD'), bSchedule: false }, { emitEvent: false });
    this.table?.reset(); void this.loadOptions();
  }
  ngOnDestroy(): void { this.destroyed = true; this.activeModal?.hide(); this.closeMoreMenu(false); this.version++; this.filterSubscription.unsubscribe(); }
  toggleMoreMenu(event: Event): void {
    if (this.loading) return;
    if (this.moreMenuOpen) { this.closeMoreMenu(); return; }
    this.moreMenuTrigger = event.currentTarget as HTMLElement;
    this.moreMenuOpen = true;
    this.moreMenuView = this.viewContainer.createEmbeddedView(this.moreMenuTemplate);
    this.moreMenuView.detectChanges();
    this.moreMenuElement = this.moreMenuView.rootNodes.find(node => node instanceof HTMLElement) as HTMLElement;
    document.body.appendChild(this.moreMenuElement);
    // Preserve the prototype's typography when the floating menu is rendered in body.
    const page = this.moreMenuTrigger.closest<HTMLElement>('.table-page');
    if (page) {
      const typography = window.getComputedStyle(page);
      this.moreMenuElement.style.fontFamily = typography.fontFamily;
      this.moreMenuElement.style.fontSize = typography.fontSize;
      const lineHeight = parseFloat(typography.lineHeight) / parseFloat(typography.fontSize);
      if (Number.isFinite(lineHeight)) this.moreMenuElement.style.lineHeight = String(lineHeight);
    }
    // Apply the prototype's left-edge fallback within the card, not just the viewport.
    const card = page?.querySelector<HTMLElement>('.card');
    const bounds = card?.getBoundingClientRect();
    const leftEdge = Math.max(8, bounds?.left ?? 8);
    const rightEdge = Math.min(window.innerWidth - 8, bounds?.right ?? window.innerWidth - 8);
    const availableWidth = Math.max(1, rightEdge - leftEdge);
    this.moreMenuElement.style.minWidth = `${Math.min(230, availableWidth)}px`;
    this.moreMenuElement.style.maxWidth = `${availableWidth}px`;
    const rect = this.moreMenuTrigger.getBoundingClientRect();
    const width = this.moreMenuElement.offsetWidth;
    const height = this.moreMenuElement.offsetHeight;
    let left = rect.right - width;
    if (left < leftEdge) left = rect.left;
    this.moreMenuElement.style.left = `${Math.max(leftEdge, Math.min(left, rightEdge - width))}px`;
    this.moreMenuElement.style.top = `${rect.bottom + height + 6 <= window.innerHeight ? rect.bottom + 6 : Math.max(8, rect.top - height - 6)}px`;
    document.addEventListener('scroll', this.closeMenuOnScroll, true);
    if (!(event instanceof MouseEvent) || event.detail === 0) this.moreMenuElement.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus();
  }
  closeMoreMenu(restoreFocus = true): void {
    this.moreMenuOpen = false;
    document.removeEventListener('scroll', this.closeMenuOnScroll, true);
    this.moreMenuView?.destroy(); this.moreMenuView = undefined;
    this.moreMenuElement?.remove(); this.moreMenuElement = undefined;
    if (restoreFocus) this.moreMenuTrigger?.focus();
    this.moreMenuTrigger = undefined;
  }
  @HostListener('document:click', ['$event'])
  onOutsideMenuClick(event: Event): void {
    if (!this.moreMenuOpen || !(event.target instanceof Node)) return;
    if (!this.moreMenuElement?.contains(event.target) && !this.moreMenuTrigger?.contains(event.target)) this.closeMoreMenu(false);
  }
  @HostListener('window:resize')
  onMenuResize(): void { this.closeMoreMenu(false); }
  @HostListener('document:keydown.escape')
  onMenuEscape(): void {
    if (this.moreMenuOpen) this.closeMoreMenu();
  }
  onMenuKeydown(event: KeyboardEvent): void {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const items = Array.from(this.moreMenuElement?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') || []);
    if (!items.length) return;
    const index = items.indexOf(document.activeElement as HTMLButtonElement);
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
    items[next].focus();
  }
  get canDelete(): boolean { return this.context.type === '21'; }
  get canSearch(): boolean { const value = this.form.getRawValue(); return !!value.strSDate && !!value.strEDate; }
  get hasOptionalFilters(): boolean {
    const value = this.form.getRawValue();
    return (value.iRoute != null && String(value.iRoute) !== '' && String(value.iRoute) !== '-1') || !!value.strUnitID || !!value.bSchedule;
  }
  get filterCount(): number {
    const value = this.form.getRawValue();
    return [value.strSDate, value.strEDate, value.iRoute != null && String(value.iRoute) !== '' && String(value.iRoute) !== '-1', value.strUnitID, value.bSchedule].filter(Boolean).length;
  }
  get title(): string { return this.definition.title; }
  get subtitle(): string { return this.loadedDates || this.dateSubtitle(); }
  get tableKey(): string { return `${this.context.type}-main`; }
  get extraSummary(): string {
    if (this.detail?.kind !== 'daily-summary') return '';
    return `تعداد روز: ${this.detailColumns.filter(column => !isNaN(Number(column.field))).length}`;
  }
  private get credentials(): Record<string, unknown> {
    return { iSystemUser: this.gurd.decrypt(localStorage.getItem('ID')!), strSession: this.gurd.decrypt(localStorage.getItem('Session')!) };
  }
  private payload(numeric = false): Record<string, unknown> {
    const value = this.form.getRawValue() as RouteSummaryFilters;
    const model = { ...this.credentials, ...value, strSDate: toEnglishDigits(value.strSDate), strEDate: toEnglishDigits(value.strEDate) };
    return numeric ? this.numericPayload(model) : model;
  }
  private numericPayload(model: Record<string, unknown>): Record<string, unknown> {
    return Object.fromEntries(Object.entries(model).map(([key, value]) => [key, key.startsWith('i') && value !== '' && Number.isFinite(Number(value)) ? Number(value) : value]));
  }
  async loadOptions(): Promise<void> {
    if (this.loading) return;
    const version = this.begin('options');
    try {
      const [routes, vehicles] = await Promise.all([
        this.api.post<RouteOption[] | RouteAnalysisStatus>('Route_List', this.credentials),
        this.api.post<VehicleOption[] | RouteAnalysisStatus>('FleetYar_Car_List_New ', {
          ID: this.credentials.iSystemUser, strSession: this.credentials.strSession,
          strUnitID: this.gurd.decrypt(localStorage.getItem('UnitID')!), bOnlyHasGPS: false, bWithIcon: false,
        }),
      ]);
      if (version !== this.version) return;
      this.routes = this.validateList(routes, 'route-list'); this.vehicles = this.validateList(vehicles, 'car-list');
    } catch (error) { if (version === this.version) this.fail(error); }
    finally { this.finish(version); }
  }
  async loadReport(): Promise<void> {
    if (this.loading || !this.canSearch) return;
    const model = this.payload(); const dates = this.dateSubtitle(); const version = this.begin('report');
    try { await this.fetchRows(model, dates, version); }
    catch (error) { if (version === this.version) { this.rows = []; this.fail(error); } }
    finally { this.finish(version); }
  }
  private async fetchRows(model: Record<string, unknown>, dates: string, version: number): Promise<void> {
    const data = await this.api.post<RouteAnalysisRow[] | RouteAnalysisStatus>('Route_FunctionReport', model);
    if (version !== this.version) return;
    this.rows = this.validateList(data, 'FN'); this.hasRequestedData = true;
    this.loadedDates = dates; this.filtersOpen = false; this.table?.reset();
  }
  async analyze(): Promise<void> {
    if (this.loading || !this.canSearch) return;
    const model = this.payload(true); const reportModel = this.payload(); const dates = this.dateSubtitle(); const version = this.begin('report');
    try {
      const result = await this.api.post<RouteAnalysisStatus>('Route_FunctionReport_Analysis', model);
      if (version !== this.version) return;
      this.checkStatus(result, 'analysis'); this.notify.showSuccess('تحلیل با موفقیت انجام شد');
      await this.fetchRows(reportModel, dates, version);
    } catch (error) { if (version === this.version) this.fail(error); }
    finally { this.finish(version); }
  }
  async deleteAnalysis(): Promise<void> {
    if (this.loading || !this.canDelete || !this.canSearch || !window.confirm('آیا از حذف تحلیل اطمینان دارید؟')) return;
    const model = this.payload(); const dates = this.dateSubtitle(); const version = this.begin('report');
    try {
      const result = await this.api.post<RouteAnalysisStatus>('Route_FunctionReport_DeleteAll', model);
      if (version !== this.version) return;
      this.checkStatus(result, 'WAxas'); this.notify.showSuccess('عملیات با موفقیت انجام شد');
      await this.fetchRows(model, dates, version);
    } catch (error) { if (version === this.version) this.fail(error); }
    finally { this.finish(version); }
  }
  async deleteRow(row: RouteAnalysisRow): Promise<void> {
    if (this.loading || !this.canDelete || row.iRoute_FunctionReport == null || !window.confirm('آیا از حذف اطمینان دارید؟')) return;
    const version = this.begin('report');
    try {
      const result = await this.api.post<RouteAnalysisStatus>('Route_FunctionReport_Delete', { ...this.credentials, iRoute_FunctionReport: row.iRoute_FunctionReport });
      if (version !== this.version) return;
      this.checkStatus(result, 'FN'); this.rows = this.rows.filter(item => item.iRoute_FunctionReport !== row.iRoute_FunctionReport);
      this.notify.showSuccess('با موفقیت حذف شد');
    } catch (error) { if (version === this.version) this.fail(error); }
    finally { this.finish(version); }
  }
  async openDetails(kind: RouteDetailKind, row?: RouteAnalysisRow): Promise<void> {
    if (this.loading || this.detailClosing || (this.detail && this.detail.kind !== kind) || (kind === 'salary' && !this.canDelete)) return;
    const rowRequest = kind === 'stations' || kind === 'passengers';
    if (rowRequest ? row?.iRoute_FunctionReport == null : !this.canSearch) return;
    const request: RouteDetailRequest = { kind, row }; const config = DETAIL_DEFINITIONS[kind];
    const model = rowRequest ? { ...this.credentials, iRoute_FunctionReport: row!.iRoute_FunctionReport } : this.payload(true);
    const payload = kind === 'passengers' ? this.numericPayload(model) : model;
    if (!this.detail) this.detailReturnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : undefined;
    this.detail = request; this.detailRows = []; this.detailColumns = detailColumns(kind, []); this.detailLoaded = false;
    this.detailDates = rowRequest ? this.loadedDates || this.dateSubtitle() : this.dateSubtitle();
    this.detailActions = rowRequest ? [STATION_MAP_ACTION] : [];
    const version = this.begin('detail');
    this.modalStates[kind] = { ...emptyRouteModalState(), requestKey: version };
    this.syncDetailState();
    try {
      this.changeDetector.detectChanges();
      $(`#${ROUTE_MODAL_IDS[kind]}`).modal('show');
      const result = await this.api.post<RouteAnalysisRow[] | RouteAnalysisStatus>(config.endpoint, payload);
      if (version !== this.version) return;
      this.detailRows = this.validateList(result, config.loginReport); this.detailColumns = detailColumns(kind, this.detailRows); this.detailLoaded = true;
    } catch (error) { if (version === this.version) this.fail(error); }
    finally { this.finish(version); if (version === this.version) this.syncDetailState(); }
  }
  onRowAction(event: RouteActionEvent): void {
    if (this.loading) return;
    if (event.action === 'delete') void this.deleteRow(event.row);
    else if (event.action === 'map') void this.openRoute(event.row);
    else if (event.action === 'station-map') this.openStationMap(event.row);
    else if (event.action === 'stations' || event.action === 'passengers') void this.openDetails(event.action, event.row);
  }
  async openRoute(row: RouteAnalysisRow): Promise<void> {
    if (this.loading || !row.strUnitID) return;
    // Open synchronously from the user gesture, then prepare storage before navigation.
    const popup = window.open('about:blank', '_blank');
    if (!popup) { this.notify.showError('لطفاً اجازه بازشدن پنجرهٔ نقشه را بدهید'); return; }
    const version = this.begin('report');
    try {
      const zones = await Promise.all([
        this.api.postRaw<unknown>('Radyab_GeoAFence', { ...this.credentials, iGeoFence: Number(row['iSourceGeoFence'] ?? -1) }),
        this.api.postRaw<unknown>('Radyab_GeoAFence', { ...this.credentials, iGeoFence: Number(row['iDestinationGeoFence'] ?? -1) }),
      ]);
      if (version !== this.version) { popup.close(); return; }
      zones.forEach((zone, index) => {
        let status = zone;
        if (typeof zone === 'string' && /^[\[{]/.test(zone.trim())) status = JSON.parse(zone);
        this.checkStatus(status, index ? 'geo-fence-y' : 'geo-fence-z');
      });
      zones.forEach((zone, index) => localStorage.setItem(`zone${index + 1}`, JSON.stringify(this.gurd.encrypt(typeof zone === 'string' ? zone : JSON.stringify(zone)))));
      this.store('RouteDate', row.strDate || ''); this.store('reportStime', row.strSourceETime || ''); this.store('reportEtime', row.strDestinationSTime || '');
      this.store('UnitID', row.strUnitID); this.store('carName', row.strFullName || ''); this.store('plate', row.strVehicleNo || '');
      popup.location.href = this.router.serializeUrl(this.router.createUrlTree([`route/${row.strUnitID}`]));
    } catch (error) { popup.close(); if (version === this.version) this.fail(error); }
    finally { this.finish(version); }
  }
  openStationMap(row: RouteAnalysisRow): void {
    if (this.loading || row.fLat == null || row.fLon == null) return;
    this.store('StopModel', JSON.stringify({ strDate: '', strAddress: '', strSTime: '', strETime: '', strParkTime: '' }));
    this.store('fLat', row.fLat); this.store('fLon', row.fLon); window.open('/stopMap', '_blank');
  }
  retry(): void {
    if (this.failedStage === 'options') void this.loadOptions();
    else if (this.failedStage === 'detail' && this.detail) void this.openDetails(this.detail.kind, this.detail.row);
    else void this.loadReport();
  }
  goBack(): void {
    this.closeMoreMenu(false);
    if (!this.detail) { this.location.back(); return; }
    this.closeDetails();
  }
  closeDetails(): void {
    this.activeModal?.hide();
  }
  onDetailClosing(kind: RouteDetailKind): void {
    if (this.destroyed || this.detail?.kind !== kind || this.detailClosing) return;
    this.version++; this.detailClosing = true;
    this.loading = true; this.form.disable({ emitEvent: false });
  }
  onDetailClosed(kind: RouteDetailKind): void {
    if (this.destroyed || this.detail?.kind !== kind) return;
    this.detail = null; this.detailClosing = false; this.loading = false;
    this.form.enable({ emitEvent: false }); this.hasError = false; this.errorMessage = ''; this.failedStage = 'report';
    this.modalStates[kind] = emptyRouteModalState();
  }
  private syncDetailState(): void {
    if (!this.detail) return;
    const kind = this.detail.kind;
    this.modalStates[kind] = {
      ...this.modalStates[kind], rows: this.detailRows, columns: this.detailColumns, actions: this.detailActions,
      subtitle: this.detailDates, extraSummary: this.extraSummary, loading: this.loading, hasError: this.hasError,
      errorMessage: this.errorMessage, hasRequestedData: this.detailLoaded, returnFocus: this.detailReturnFocus,
    };
  }
  toggleFilters(): void { if (!this.loading) this.filtersOpen = !this.filtersOpen; }
  clearFilters(): void {
    if (this.loading) return;
    const today = momentJalaali().format('jYYYY/jMM/jDD');
    this.form.reset({ iRoute: -1, strUnitID: '', strSDate: today, strEDate: today, bSchedule: false }, { emitEvent: false });
    this.rows = []; this.hasRequestedData = false; this.hasError = false; this.loadedDates = ''; this.filtersOpen = true; this.table?.reset();
  }
  private begin(stage: 'options' | 'report' | 'detail'): number {
    this.closeMoreMenu(false);
    this.failedStage = stage; this.hasError = false; this.errorMessage = ''; this.loading = true;
    this.form.disable({ emitEvent: false }); return ++this.version;
  }
  private finish(version: number): void { if (version === this.version) { this.loading = false; this.form.enable({ emitEvent: false }); } }
  private validateList<T>(data: T[] | RouteAnalysisStatus, report: string): T[] {
    this.checkStatus(data, report); if (!Array.isArray(data)) throw new Error('پاسخ سرور معتبر نیست'); return data;
  }
  private checkStatus(data: unknown, report: string): void {
    if (data == null) throw new Error('پاسخ سرور معتبر نیست');
    const status = (Array.isArray(data) ? data[0] : data) as RouteAnalysisStatus | undefined;
    if (status?.ISSessionExpired || status?.ISessionExpired) {
      this.other.removeLocalStorage(); void this.router.navigate(['login'], { queryParams: { report } }); throw new Error('لطفا وارد سیستم شوید');
    }
    if (status?.bError || status?.iResult === 0) throw new Error(status.strError || 'عملیات با خطا مواجه شد');
  }
  private fail(error: unknown): void { this.hasError = true; this.errorMessage = error instanceof Error ? error.message : 'دریافت اطلاعات با خطا مواجه شد'; this.notify.showError(this.errorMessage); }
  private dateSubtitle(): string { const value = this.form.getRawValue(); return `از تاریخ ${value.strSDate} الی ${value.strEDate}`; }
  private store(key: string, value: string | number): void { localStorage.setItem(key, this.gurd.encrypt(value)); }
}
